/**
 * Reconocimiento de los rechazos explícitos de los callables de facturación.
 *
 * ## Por qué existe
 *
 * `generateInvoice` valida dentro de la transacción que ningún movimiento esté
 * ya facturado y, si lo está, rechaza con `HttpsError('failed-precondition')`.
 * Antes ese caso no existía: el servidor emitía una segunda factura con el mismo
 * importe y alguien tenía que rectificarla a mano.
 *
 * Un rechazo así **no es un fallo de infraestructura**: el usuario puede
 * resolverlo (refrescar la lista y facturar solo lo pendiente), así que merece
 * un mensaje distinto del genérico «Ha ocurrido un error». Este predicado es lo
 * que permite distinguirlos en el cliente.
 *
 * ## Por qué no se compara el código a secas
 *
 * El SDK de Firebase Functions para web prefija el código del `HttpsError` con
 * `functions/` (`functions/failed-precondition`), mientras que el callable en
 * servidor y el SDK de Node lo exponen sin prefijo. Se aceptan ambas formas para
 * que el mismo helper sirva en `apps/admin`, `apps/app` y en los tests.
 *
 * Módulo puro, sin imports: testeable con el runner file-based de Playwright
 * (`--project=functions`), igual que `billing-party.ts`.
 */

/** Código del rechazo que emite `generateInvoice` ante un movimiento ya facturado. */
export const ALREADY_INVOICED_ERROR_CODE = 'failed-precondition';

/** Código del rechazo que emite `deleteInvoice` cuando la factura ya no está. */
export const NOT_FOUND_ERROR_CODE = 'not-found';

/** Forma mínima de un error de callable, sea del SDK web o del de Node. */
export interface CallableErrorLike {
  code?: unknown;
  message?: unknown;
}

/**
 * Extrae el código de un error de callable sin el prefijo `functions/`.
 * Devuelve `''` si el error no trae un código utilizable.
 */
export function getCallableErrorCode(error: unknown): string {
  const code = (error as CallableErrorLike | null | undefined)?.code;
  if (typeof code !== 'string') {
    return '';
  }
  return code.startsWith('functions/') ? code.slice('functions/'.length) : code;
}

/**
 * `true` si el callable rechazó porque alguno de los movimientos enviados ya
 * estaba facturado. El llamador debe mostrar un mensaje accionable («actualiza
 * la lista y factura solo lo pendiente»), no el error genérico.
 */
export function isAlreadyInvoicedError(error: unknown): boolean {
  return getCallableErrorCode(error) === ALREADY_INVOICED_ERROR_CODE;
}

/**
 * `true` si el callable rechazó porque el documento ya no existe. `deleteInvoice`
 * lo lanza cuando la factura fue borrada entre que se pintó la lista y se
 * confirmó el diálogo: es el caso de las dos pestañas abiertas, y el usuario lo
 * resuelve refrescando, así que merece un mensaje propio y no el genérico.
 */
export function isNotFoundError(error: unknown): boolean {
  return getCallableErrorCode(error) === NOT_FOUND_ERROR_CODE;
}
